import { Fragment, useMemo } from "react";
import { cn } from "@/lib/utils";
import { ProDriverSupplierCityMovement } from "@/app/Reports/ProDriverSupplierReport/GraphQL/proDriverSupplierReportQuery";

interface Props {
  movements: ProDriverSupplierCityMovement[];
}

export function ProDriverSupplierCityMatrix({ movements }: Props) {
  const uniqueCities = useMemo(() => {
    const cities = new Set<string>();
    movements.forEach((m) => {
      cities.add(m.departure_city);
      cities.add(m.delivery_city);
    });
    return Array.from(cities).sort((a, b) => a.localeCompare(b));
  }, [movements]);

  const movementMap = useMemo(() => {
    const map = new Map<string, number>();
    movements.forEach((m) => {
      map.set(`${m.departure_city}-${m.delivery_city}`, m.count);
    });
    return map;
  }, [movements]);

  const maxCount = Math.max(...movements.map((m) => m.count), 1);

  function getHeatColor(count: number) {
    if (count === 0) {
      return "bg-gray-50";
    }
    const intensity = count / maxCount;
    if (intensity > 0.75) {
      return "bg-indigo-600 text-white";
    }
    if (intensity > 0.5) {
      return "bg-indigo-400 text-white";
    }
    if (intensity > 0.25) {
      return "bg-indigo-200";
    }
    return "bg-indigo-100";
  }

  if (uniqueCities.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        No work order routes for this period
      </div>
    );
  }

  const numCols = uniqueCities.length;
  const numRows = uniqueCities.length;

  return (
    <div className="p-4">
      <div
        className="grid max-h-[400px] overflow-auto rounded-lg border"
        style={{
          gridTemplateColumns: `8rem repeat(${numCols}, minmax(4rem, 1fr))`,
          gridTemplateRows: `3rem repeat(${numRows}, 3rem)`,
        }}
      >
        {/* Top-left corner */}
        <div className="sticky left-0 top-0 z-20 flex items-center justify-center border-b border-r bg-gray-100 text-xs font-medium text-gray-500">
          Depart \ Deliver
        </div>

        {/* Column headers (delivery cities) */}
        {uniqueCities.map((city) => (
          <div
            key={`col-${city}`}
            className="sticky top-0 z-10 flex items-center border-b bg-gray-100 px-2 text-xs font-medium"
            title={city}
          >
            <span className="truncate">{city}</span>
          </div>
        ))}

        {/* Rows */}
        {uniqueCities.map((rowCity) => (
          <Fragment key={rowCity}>
            <div
              className="sticky left-0 z-10 flex items-center border-r bg-gray-100 px-2 text-xs font-medium"
              title={rowCity}
            >
              <span className="truncate">{rowCity}</span>
            </div>

            {uniqueCities.map((colCity) => {
              const count = movementMap.get(`${rowCity}-${colCity}`) ?? 0;

              return (
                <div
                  key={`${rowCity}-${colCity}`}
                  className={cn(
                    "flex items-center justify-center border-b border-r text-sm",
                    getHeatColor(count),
                  )}
                  title={`${rowCity} -> ${colCity}: ${count}`}
                >
                  {count > 0 ? count : ""}
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
